// ⭐ THE PRE-MATCH COMBO SPACE IS PRICED OFF THE RENDER PATH.
// A pre-match price is not moving under the visitor, so a verdict holds for
// minutes. The render shows only cards with an `ok` verdict. Unpriced cards are
// held back and priced after the render, one at a time, and they appear on the
// next poll. A shown card has therefore always been accepted by the operator.

export type SpaceVerdict = "ok" | "refused" | "no-value" | "dead-leg";
export interface SpaceEntry { at: number; factor: number; verdict: SpaceVerdict }

export const PREMATCH_VERDICT_TTL_MS = 10 * 60_000;
export const LIVE_VERDICT_TTL_MS = 45_000;

type LegId = { market_id?: number | null; code?: number | null; line?: number | string | null; stake_id?: string; part?: string | null };

function num(x: unknown): string {
  if (x == null || x === "") return "";
  const f = Number(x);
  return Number.isFinite(f) ? String(f) : String(x);
}

/** Rotation-safe identity of a combo: `(market_id, code, line)` per leg, sorted,
 *  plus `@<part>` for a leg that is NOT on the main book.
 *  ⛔ Must equal `bb_verdicts.legs_key` in the backend — both tests assert
 *  `40343484|1:1:,3:9:2.5`.
 *
 *  ⛔⛔ WHY `@part` EXISTS. Every sub-event of a Digitain fixture re-uses the
 *  SAME market ids, so a corners total is `(3, 1, 9.5)` and a goals Over 9.5 is
 *  `(3, 1, 9.5)` — one key for two different bets. MEASURED on a live
 *  /Betting/PlaceBet coupon 2026-09-20 (`StakeTypeId 3`, `Argument 9.5`,
 *  `EventPartTypeId 4`).
 *  ⛔ The suffix is OMITTED on the main book, so every key ever stored in
 *  `bb_combo_verdicts` still matches and the pinned `40343484|1:1:,3:9:2.5`
 *  is unchanged. A part is only ever ADDED to a leg that has one. */
export function outcomeKey(eventId: number | string, legs: LegId[]): string {
  const parts = legs.map((l) => (l.market_id == null
    ? "s:" + String(l.stake_id ?? "")
    : `${num(l.market_id)}:${num(l.code)}:${num(l.line)}${partSuffix(l.part)}`));
  return String(eventId) + "|" + parts.sort().join(",");
}

/** `@4:1` for a sub-event leg, `""` for the main book. ⛔ `"0:0"` is the main
 *  book spelled out and must produce NO suffix, or a backend that sends it
 *  explicitly would key differently from one that omits it. */
export function partSuffix(part: string | null | undefined): string {
  const p = String(part ?? "").trim();
  return !p || p === "0:0" ? "" : "@" + p;
}

export class BbSpace {
  private m = new Map<string, SpaceEntry>();
  get(key: string, isLive: boolean, now = Date.now()): SpaceEntry | null {
    const e = this.m.get(key);
    if (!e) return null;
    const ttl = isLive ? LIVE_VERDICT_TTL_MS : PREMATCH_VERDICT_TTL_MS;
    return now - e.at <= ttl ? e : null;
  }
  set(key: string, e: SpaceEntry): void { this.m.set(key, e); }
}

/** ⭐ SEED THE SPACE WITH THE BOOK'S ANSWER FROM ANOTHER BROWSER.
 *  The verdict is per FIXTURE, not per visitor (`bb_verdicts.py`), but this map
 *  starts EMPTY on every page load — so a combo the operator already accepted is
 *  held off the render until this tab re-prices it, and the visitor is almost
 *  never still looking when that lands (measured 2026-09-18: 6,427 accepted
 *  pre-match accas in two days, 12 renders). `/suggestions` now serves
 *  `bb_verdict: "ok"` with the operator's own `bb_factor` and the row's age, and
 *  the entry is stored at its REAL age, so `PREMATCH_VERDICT_TTL_MS` expires a
 *  shared verdict at the same moment it would expire one we priced ourselves.
 *  ⛔ A verdict this tab produced always wins: it is this visitor's own board.
 *  Returns true when the space gained the entry. */
export function seedServed(
  s: { is_live?: boolean | null; bb_verdict?: string | null; bb_factor?: number | null; bb_age_s?: number | null },
  key: string, space: BbSpace, now = Date.now(),
): boolean {
  if (s.is_live !== false || s.bb_verdict !== "ok") return false;
  const factor = Number(s.bb_factor);
  if (!Number.isFinite(factor) || factor <= 0) return false;
  const age = Number(s.bb_age_s);
  const at = now - (Number.isFinite(age) && age > 0 ? age * 1000 : 0);
  if (now - at > PREMATCH_VERDICT_TTL_MS) return false;
  const held = space.get(key, false, now);
  if (held && held.at >= at) return false;
  space.set(key, { at, factor, verdict: "ok" });
  return true;
}

/** The render's decision for one combo card. Pre-match (`is_live === false`)
 *  shows only an `ok` verdict already in hand; no verdict → queue for the drain;
 *  any other verdict → hold. Everything else takes the inline live path. */
export function prematchDecision(
  s: { is_live?: boolean | null }, key: string, space: BbSpace, now?: number,
): "live" | "queue" | "hold" | { show: number } {
  if (s.is_live !== false) return "live";
  const e = space.get(key, false, now);
  if (!e) return "queue";
  if (e.verdict !== "ok") return "hold";
  return { show: e.factor };
}

/** Held cards leave the render silently; dropped cards leave it and are
 *  returned for the one `bet-builder-dropped-preview` event. */
export function settleRender<T>(list: T[], cards: T[]): T[] {
  const f = (s: T) => s as unknown as Record<string, unknown>;
  for (let i = list.length - 1; i >= 0; i--) {
    if (f(list[i]).bb_hold) list.splice(i, 1);
  }
  const dropped = cards.filter((s) => f(s).bb_drop);
  for (const s of dropped) {
    const i = list.indexOf(s);
    if (i >= 0) list.splice(i, 1);
  }
  return dropped;
}

/** The best operator price among EVERY pair inside a 3-leg combo. A triple that
 *  pays no more than this is a two-leg bet wearing three legs (`bbVerdict` →
 *  `dead-leg`). ⛔ Not the first two legs in card order: measured 2026-09-15, 42 of
 *  49 accepted triples were priced exactly like a pair the old first-pair check
 *  never asked about. Unpriced pairs are ignored; all unpriced → null ("could not
 *  tell"). Pairs are asked one at a time — `priceViaService` is serialized anyway. */
export async function bestSubPairFactor(
  legs: number[], price: (pair: number[]) => Promise<number | null>,
): Promise<number | null> {
  if (legs.length < 3) return null;
  let best: number | null = null;
  for (let i = 0; i < legs.length; i++) {
    for (let j = i + 1; j < legs.length; j++) {
      const f = await price([legs[i], legs[j]]);
      if (f != null && (best == null || f > best)) best = f;
    }
  }
  return best;
}

let draining = false;

export async function drainSpace<T>(
  queue: T[], keyOf: (c: T) => string, space: BbSpace,
  priceOne: (c: T) => Promise<SpaceEntry | null>,
  emit: (c: T, key: string, e: SpaceEntry) => void,
): Promise<number> {
  if (draining) return 0;
  draining = true;
  let stored = 0;
  const done = new Set<string>();
  try {
    while (queue.length) {
      const c = queue.shift()!;
      const key = keyOf(c);
      if (done.has(key) || space.get(key, false)) continue;
      done.add(key);
      const e = await priceOne(c);
      if (!e) continue;                       // could not tell — retry on a later poll
      space.set(key, e);
      emit(c, key, e);
      stored++;
    }
  } finally {
    draining = false;
  }
  return stored;
}
